import { Component, OnInit, ElementRef, ViewChild, AfterViewInit, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { SlimLoadingBarService } from 'ng2-slim-loading-bar';
import { ProjectsService } from '../services/projects/projects.service';
import { ReservationService } from '../services/reservation-service/reservation.service';
import { PaymentService } from '../services/payment/payment.service';
import { UserServiceService } from '../services/user-service/user-service.service';
import { ImageZoomService } from '../shared/image-zoom/image-zoom.service';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { NotificationsService as SimpleNotificationsService } from 'angular2-notifications';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/distinctUntilChanged';
import swal from 'sweetalert2';
import { environment } from '../../environments/environment';

interface MasterplanLevel {
  key: string;
  label: string;
  svgPath: string;
  floorFolder: string | null;
  imageFilePrefix: string | null;
}

interface BlockMapping {
  blockId: string;
  blockNumber: string;
  expectedUnitSerial?: string;
  unitId?: number;
  unitSerial?: string;
  unitArea?: number;
  unitBuilding?: string;
  unitFloor?: string;
  unitStatus?: string;
  unitPrice?: number;
  unitPricePerMeter?: number;
  unitGaragePrice?: number;
  unitMaintenanceFees?: number;
  unitPrices?: UnitPriceEntry[];
}

interface StatusLegendItem {
  label: string;
  color: string;
  textColor?: string;
}

interface HoverTooltipState {
  blockId: string;
  unitSerial: string;
  status: string;
  price?: number;
  pricePerMeter?: number;
  garagePrice?: number;
  maintenanceFees?: number;
  prices?: UnitPriceEntry[];
  x: number;
  y: number;
}

interface UnitPriceEntry {
  label: string;
  value: number;
  pricePerMeter?: number | null;
}

interface InlinePaymentFormModel {
  finishing_type_id: number | null;
  type: any;
  slots: number;
  service_room_slots: number;
  service_room_with_bathroom_slots: number;
  generated_view: string;
  price: any;
  percentage: any;
  status: string;
  extra_discount: any;
  starting_date: string;
  phase_id: any;
  unit_serial: string;
}

@Component({
  selector: 'app-setup-dynamic-masterplan',
  templateUrl: './setup-dynamic-masterplan.component.html',
  styleUrls: ['./setup-dynamic-masterplan.component.css']
})
export class SetupDynamicMasterplanComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('svgContainer') svgContainer: ElementRef;
  @Input() projectId: number;
  projectName: string = '';

  masterplans: MasterplanLevel[] = [
    {
      key: 'lower-ground',
      label: 'Lower Ground Floor',
      svgPath: '/assets/SVG/lower ground floor.svg',
      floorFolder: '1-LOWER GROUND FLOOR',
      imageFilePrefix: '1-CENRADA Rise projedt 2026 rev11 (15-04-2026 )- '
    },
    {
      key: 'upper-ground',
      label: 'Upper Ground Floor',
      svgPath: '/assets/SVG/upper ground floor.svg',
      floorFolder: null,
      imageFilePrefix: null
    },
    {
      key: 'floor-1',
      label: 'Floor 1',
      svgPath: '/assets/SVG/first floor.svg',
      floorFolder: null,
      imageFilePrefix: null
    },
    {
      key: 'floor-2',
      label: 'Floor 2',
      svgPath: '/assets/SVG/second floor.svg',
      floorFolder: null,
      imageFilePrefix: null
    },
    {
      key: 'floor-3',
      label: 'Floor 3',
      svgPath: '/assets/SVG/third floor.svg',
      floorFolder: null,
      imageFilePrefix: null
    },
    {
      key: 'floor-4',
      label: 'Floor 4',
      svgPath: '/assets/SVG/fourth floor.svg',
      floorFolder: null,
      imageFilePrefix: null
    },
    {
      key: 'floor-5',
      label: 'Floor 5',
      svgPath: '/assets/SVG/fifth floor.svg',
      floorFolder: null,
      imageFilePrefix: null
    },
    {
      key: 'floor-6',
      label: 'Floor 6',
      svgPath: '/assets/SVG/Sixth floor.svg',
      floorFolder: null,
      imageFilePrefix: null
    },
  ];
  readonly statusLegend: StatusLegendItem[] = [
    { label: 'Available', color: '#22c55e' },
    { label: 'Not Available', color: '#f97316' },
    { label: 'Blocked', color: '#ef4444' },
    { label: 'Temp Blocked', color: '#facc15', textColor: '#1f2937' },
    { label: 'Reserved', color: '#8b5cf6' },
    { label: 'Blocked By Me', color: '#0ea5e9' },
    { label: 'Pending Finance Action', color: '#a16207' },
    { label: 'Re-pricing', color: '#14b8a6' }
  ];
  private readonly interactiveShapeSelector = 'path, polygon, polyline, rect, circle, ellipse';

  selectedMasterplanKey: string = 'lower-ground';
  svgAssetPath: string = this.masterplans[0].svgPath;
  svgContent: string = '';
  isLoading: boolean = true;
  svgLoadError: string | null = null;
  svgInteractivityWarning: string | null = null;
  selectedBlock: BlockMapping | null = null;
  showModal: boolean = false;
  showViewModal: boolean = false;
  availableUnits: any[] = [];
  selectedUnitId: number | null = null;
  blockMappings: Map<string, BlockMapping> = new Map();
  interactiveMode: 'BLOCK_NUM' | 'LWPOLYLINE' | 'CO_SALES' | 'FLAT_SHAPES' | 'NONE' = 'NONE';
  isEditMode: boolean = false;
  isUnitSearchLoading: boolean = false;
  unitSearchError: string | null = null;
  saveError: string | null = null;
  isViewUnitLoading: boolean = false;
  stopReservation: boolean = false;
  unitDetails: any = null;
  blockSettingsTimes: any[] = [];
  blockSettingsReasons: any[] = [];
  blockSettingsAgents: any[] = [];
  blockFormModel: any = {
    time: '',
    reason: '',
    user_id: '',
    reason_comment: ''
  };
  unitComment: string = '';
  activeActionPanel: 'block' | 'comment' | null = null;
  paymentMethods: any[] = [];
  inlinePaymentForm: InlinePaymentFormModel = this.createInlinePaymentForm();
  showInlinePaymentForm: boolean = false;
  inlinePaymentGenerated: boolean = false;
  inlinePaymentLoading: boolean = false;
  inlinePaymentError: string | null = null;
  generatingInlineOfferPdf: boolean = false;
  currentLayoutSlide: number = 0;
  readonly unitSearch$ = new Subject<string>();
  private svgWithDelegatedHandlers: SVGSVGElement | null = null;
  private svgPointerOverHandler: ((ev: PointerEvent) => void) | null = null;
  private svgPointerOutHandler: ((ev: PointerEvent) => void) | null = null;
  private svgPointerMoveHandler: ((ev: PointerEvent) => void) | null = null;
  private svgClickHandler: ((ev: MouseEvent) => void) | null = null;
  private hoveredInteractiveElement: SVGElement | null = null;
  private hoveredPathElement: SVGElement | null = null;
  private selectedInteractiveElement: SVGElement | null = null;
  private unitSearchSubscription: Subscription | null = null;
  private readonly truthySalesTeamFlags = [true, 1, '1', 'true'];
  private readonly unitStatusActionEmails = [
    'fabricasupport@narrativecommunities.com',
    'mahmoud.abdelhakim@biographydev.com',
    'hisham@narrativecommunities.com',
  ];
  private svgStateSyncTimer: any = null;
  private isSvgAssetLoading: boolean = false;
  private isMappingsLoading: boolean = false;
  private hasSvgMarkupLoaded: boolean = false;
  private hasMappingsLoaded: boolean = false;
  private svgRequestSeq: number = 0;
  private mappingsRequestSeq: number = 0;
  private finalizeSvgSetupTimer: any = null;
  hoverTooltip: HoverTooltipState | null = null;
  userProfile: any = JSON.parse(window.localStorage.getItem('userProfile') || 'null');

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private slimLoadingBarService: SlimLoadingBarService,
    private reservationService: ReservationService,
    private projectsService: ProjectsService,
    private notificationService: SimpleNotificationsService,
    private paymentService: PaymentService,
    private userService: UserServiceService,
    private imageZoomService: ImageZoomService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && changes['projectId'].currentValue) {
      this.loadMasterPlanMappings();
    }
  }

  ngOnInit(): void {
    if (!this.projectId) {
      this.route.params.subscribe(params => {
        if (params['id']) {
          this.projectId = +params['id'];
          this.loadMasterPlanMappings();
        }
      });

      this.route.queryParams.subscribe(params => {
        this.projectName = params['project_name'] || this.projectName;
        if (params['id'] && !this.projectId) {
          this.projectId = +params['id'];
          this.loadMasterPlanMappings();
        }
      });
    }

    this.unitSearchSubscription = this.unitSearch$
      .debounceTime(250)
      .distinctUntilChanged()
      .subscribe((term: string) => this.fetchAvailableUnits(term));

    this.loadBlockSettings();
  }

  ngAfterViewInit(): void {
    this.loadSvg();
  }

  ngOnDestroy(): void {
    if (this.unitSearchSubscription) {
      this.unitSearchSubscription.unsubscribe();
    }
    if (this.svgStateSyncTimer) {
      clearTimeout(this.svgStateSyncTimer);
      this.svgStateSyncTimer = null;
    }
    if (this.finalizeSvgSetupTimer) {
      clearTimeout(this.finalizeSvgSetupTimer);
      this.finalizeSvgSetupTimer = null;
    }
    this.detachDelegatedSvgHandlers();
  }

  selectMasterplan(key: string): void {
    if (this.selectedMasterplanKey === key) {
      return;
    }

    this.selectedMasterplanKey = key;
    const selected = this.masterplans.find(p => p.key === key);
    if (!selected) {
      return;
    }

    this.svgAssetPath = selected.svgPath;
    this.closeModal();
    this.clearSelectedElement();
    this.resetFloorLoadState();
    this.blockMappings = new Map(); // cleared here; loadMasterPlanMappings will repopulate
    this.interactiveMode = 'NONE';
    this.loadMasterPlanMappings();
    this.loadSvg();
  }

  setEditMode(isEditMode: boolean): void {
    if (!this.canAccessModeToggle()) {
      this.isEditMode = false;
      return;
    }

    this.isEditMode = !!isEditMode;
    if (!this.isEditMode) {
      this.closeModal();
      this.clearSelectedElement();
    }

    this.clearHoveredPath();
    this.scheduleSvgStateSync();
  }

  private extractSvgMarkup(markup: string): string | null {
    if (!markup) {
      return null;
    }
    const start = markup.indexOf('<svg');
    if (start === -1) {
      return null;
    }
    const end = markup.lastIndexOf('</svg>');
    if (end === -1) {
      return null;
    }
    return markup.slice(start, end + '</svg>'.length);
  }

  loadSvg(): void {
    const requestId = ++this.svgRequestSeq;
    this.svgLoadError = null;
    this.svgInteractivityWarning = null;
    this.isSvgAssetLoading = true;
    this.hasSvgMarkupLoaded = false;
    this.updateLoadingState();
    this.svgContent = '';
    this.slimLoadingBarService.start();
    this.http.get(this.svgAssetPath, { responseType: 'text' })
      .subscribe(
        (svgData) => {
          if (requestId !== this.svgRequestSeq) {
            return;
          }
          const extracted = this.extractSvgMarkup(svgData);
          if (!extracted) {
            this.svgContent = '';
            this.svgLoadError = `SVG loaded but could not find <svg> tag in: ${this.svgAssetPath}`;
            this.isSvgAssetLoading = false;
            this.updateLoadingState();
            this.slimLoadingBarService.complete();
            return;
          }

          this.svgContent = extracted;
          this.hasSvgMarkupLoaded = true;
          this.isSvgAssetLoading = false;
          this.updateLoadingState();
          this.slimLoadingBarService.complete();
          this.tryFinalizeSvgSetup();
        },
        (error) => {
          if (requestId !== this.svgRequestSeq) {
            return;
          }
          this.svgContent = '';
          const status = error && error.status ? ` (HTTP ${error.status})` : '';
          this.svgLoadError = `Error loading SVG${status}: ${this.svgAssetPath}`;
          this.isSvgAssetLoading = false;
          this.updateLoadingState();
          this.slimLoadingBarService.complete();
        }
      );
  }

  loadMasterPlanMappings(): void {
    if (!this.projectId) {
      return;
    }
    const requestId = ++this.mappingsRequestSeq;
    const level = this.selectedMasterplanKey;
    this.isMappingsLoading = true;
    this.hasMappingsLoaded = false;
    this.updateLoadingState();
    this.blockMappings = new Map();
    this.projectsService.getMasterplanProjectMappings(this.projectId, level).subscribe(
      (res: any) => {
        if (requestId !== this.mappingsRequestSeq || level !== this.selectedMasterplanKey) {
          return;
        }
        const mappings = res.data || res || [];
        const nextMappings = new Map<string, BlockMapping>(this.blockMappings);
        mappings.forEach((m: any) => {
          const blockId = `${m.path_id || ''}`;
          const blockNumber = this.extractBlockNumber(blockId) || blockId.replace(/^F/i, '');
          const mappedUnit = m.unit || {};
          const unitPrices = this.getUnitPriceEntries(mappedUnit);
          nextMappings.set(m.path_id, {
            blockId,
            blockNumber,
            expectedUnitSerial: this.getExpectedUnitSerial(blockNumber),
            unitId: m.unit_id,
            unitSerial: m.unit_serial,
            unitArea: m.unit_area,
            unitBuilding: m.unit_building,
            unitFloor: m.unit_floor,
            unitStatus: m.unit_status || mappedUnit.status || undefined,
            unitPrice: unitPrices.length ? unitPrices[0].value : mappedUnit.price,
            unitPricePerMeter: unitPrices.length ? unitPrices[0].pricePerMeter : mappedUnit.price_per_meter,
            unitGaragePrice: mappedUnit.project && mappedUnit.project.garage_price,
            unitMaintenanceFees: mappedUnit.maintenance_fees,
            unitPrices: unitPrices,
          });
        });
        this.blockMappings = nextMappings;
        this.isMappingsLoading = false;
        this.hasMappingsLoaded = true;
        this.updateLoadingState();
        this.tryFinalizeSvgSetup();
      },
      () => {
        if (requestId !== this.mappingsRequestSeq) {
          return;
        }
        if (level === this.selectedMasterplanKey) {
          this.blockMappings = new Map();
          this.isMappingsLoading = false;
          this.hasMappingsLoaded = true;
          this.updateLoadingState();
          this.tryFinalizeSvgSetup();
        }
      }
    );
  }

  private resetFloorLoadState(): void {
    this.isSvgAssetLoading = false;
    this.isMappingsLoading = false;
    this.hasSvgMarkupLoaded = false;
    this.hasMappingsLoaded = false;
    if (this.finalizeSvgSetupTimer) {
      clearTimeout(this.finalizeSvgSetupTimer);
      this.finalizeSvgSetupTimer = null;
    }
    this.updateLoadingState();
  }

  private updateLoadingState(): void {
    this.isLoading = this.isSvgAssetLoading || this.isMappingsLoading;
  }

  private tryFinalizeSvgSetup(): void {
    if (!this.hasMappingsLoaded || !this.hasSvgMarkupLoaded || this.isLoading || this.svgLoadError) {
      return;
    }

    if (this.finalizeSvgSetupTimer) {
      clearTimeout(this.finalizeSvgSetupTimer);
    }

    this.finalizeSvgSetupTimer = setTimeout(() => {
      this.finalizeSvgSetupTimer = null;
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.setupSvgInteractivity();
          this.scheduleSvgStateSync();
        });
      });
    }, 0);
  }

  private fetchAvailableUnits(term: string): void {
    const query = (term || '').trim();
    if (!this.projectId) {
      return;
    }

    if (!query) {
      this.availableUnits = this.selectedBlock && this.selectedBlock.unitId
        ? [this.toUnitOption(this.selectedBlock)]
        : [];
      this.unitSearchError = null;
      this.isUnitSearchLoading = false;
      return;
    }

    const level = this.selectedMasterplanKey;
    this.isUnitSearchLoading = true;
    this.unitSearchError = null;
    this.projectsService.searchMasterplanProjectUnits(this.projectId, {
      q: query,
      level,
      limit: 20
    })
      .subscribe(
        (res: any) => {
          if (level !== this.selectedMasterplanKey) {
            return;
          }
          this.availableUnits = this.mergeUnitsWithSelected(res.data || res || []);
          this.isUnitSearchLoading = false;
        },
        () => {
          if (level !== this.selectedMasterplanKey) {
            return;
          }
          this.availableUnits = [];
          this.unitSearchError = 'Unit search failed. Try again.';
          this.isUnitSearchLoading = false;
        }
      );
  }

  setupSvgInteractivity(): void {
    const container = this.svgContainer && this.svgContainer.nativeElement;
    if (!container) {
      return;
    }

    const svg = (container as HTMLElement).querySelector('svg') as SVGSVGElement | null;
    if (!svg) {
      this.svgLoadError = this.svgLoadError || 'SVG injected but <svg> element not found in DOM.';
      return;
    }

    this.applyResponsiveSvgSizing(svg);

    // 1. Try Inkscape CO SALES layer groups (CENRADA Rise SVG format)
    const allGroups = Array.from(svg.querySelectorAll('g'));
    const coSalesGroups = allGroups.filter(g => g.getAttribute('inkscape:label') === '1 CO SALES') as SVGGElement[];
    if (coSalesGroups.length) {
      this.interactiveMode = 'CO_SALES';
      this.bindCoSalesMode(coSalesGroups);
      this.scheduleSvgStateSync();
      this.attachDelegatedSvgHandlers(svg);
      this.svgInteractivityWarning = null;
      return;
    }

    // 2. Fall back to BLOCK_NUM
    const blockNumGroup = svg.querySelector('[id*="BLOCK_NUM"]') as SVGGElement | null;
    if (blockNumGroup) {
      this.interactiveMode = 'BLOCK_NUM';
      this.bindBlockNumMode(blockNumGroup);
      this.scheduleSvgStateSync();
      this.attachDelegatedSvgHandlers(svg);
      this.svgInteractivityWarning = null;
      return;
    }

    // 3. Fall back to LWPOLYLINE
    const lwpolylineGroups = Array.from(svg.querySelectorAll('g[id^="LWPOLYLINE"]') as NodeListOf<SVGGElement>);
    if (lwpolylineGroups.length) {
      this.interactiveMode = 'LWPOLYLINE';
      this.bindPolylineMode(lwpolylineGroups, svg);
      this.scheduleSvgStateSync();
      this.attachDelegatedSvgHandlers(svg);
      this.svgInteractivityWarning = null;
      return;
    }

    // 4. Fall back to unit shapes stored directly in SVG layers.
    const flatLayerShapes = this.getInteractiveFlatShapes(svg);
    if (flatLayerShapes.length) {
      this.interactiveMode = 'FLAT_SHAPES';
      this.bindFlatShapeMode(svg, flatLayerShapes);
      this.scheduleSvgStateSync();
      this.attachDelegatedSvgHandlers(svg);
      this.svgInteractivityWarning = null;
      return;
    }

    this.interactiveMode = 'NONE';
    this.blockMappings = new Map();
    this.svgInteractivityWarning = 'No interactive groups found in this SVG.';
  }

  private applyResponsiveSvgSizing(svg: SVGSVGElement): void {
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.removeAttribute('width');
    svg.removeAttribute('height');
    svg.style.width = '100%';
    svg.style.height = 'auto';

    const hasViewBox = svg.hasAttribute('viewBox');
    if (hasViewBox) {
      return;
    }

    const widthAttr = svg.getAttribute('width') || '';
    const heightAttr = svg.getAttribute('height') || '';
    const width = parseFloat(widthAttr);
    const height = parseFloat(heightAttr);

    if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
      return;
    }

    try {
      const bbox = svg.getBBox();
      if (bbox && bbox.width > 0 && bbox.height > 0) {
        svg.setAttribute('viewBox', `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);
      }
    } catch (e) {
      return;
    }
  }

  private bindCoSalesMode(groups: SVGGElement[]): void {
    const existingMappings = new Map(this.blockMappings);
    this.blockMappings = new Map();

    groups.forEach((group: SVGGElement, index: number) => {
      const blockNumber = this.padBlockNumber(index + 1);
      const blockId = `F${blockNumber}`;

      group.id = blockId;
      group.classList.add('interactive-block');
      group.setAttribute('data-block-number', blockNumber);
      group.style.cursor = 'pointer';
      group.style.pointerEvents = 'all';

      // Preserve any API-loaded mapping for this blockId
      const existing = existingMappings.get(blockId);
      this.blockMappings.set(blockId, this.buildBlockMappingFromExisting(blockId, blockNumber, existing));
    });
  }

  private bindBlockNumMode(blockNumGroup: SVGGElement): void {
    const existingMappings = new Map(this.blockMappings);
    const blockGroups = Array.from(blockNumGroup.children).filter((el): el is SVGGElement => el instanceof SVGGElement);

    this.blockMappings = new Map();

    blockGroups.forEach((group: SVGGElement) => {
      const blockId = group.id;
      const blockNumber = this.extractBlockNumber(blockId, group);
      if (!blockNumber) {
        return;
      }

      const existing = existingMappings.get(blockId);
      this.blockMappings.set(blockId, this.buildBlockMappingFromExisting(blockId, blockNumber, existing));
      group.classList.add('interactive-block');
      group.setAttribute('data-block-number', blockNumber);
      group.style.cursor = 'pointer';
      group.style.pointerEvents = 'all';
    });
  }

  private bindPolylineMode(groups: SVGGElement[], svg: SVGSVGElement): void {
    const existingMappings = new Map(this.blockMappings);
    this.blockMappings = new Map();

    groups.forEach((group: SVGGElement, index: number) => {
      const blockNumber = this.padBlockNumber(index + 1);
      const blockId = group.id || `F${blockNumber}`;
      const existing = existingMappings.get(blockId);

      this.blockMappings.set(blockId, this.buildBlockMappingFromExisting(blockId, blockNumber, existing));
      group.id = blockId;
      group.classList.add('interactive-block');
      group.setAttribute('data-block-number', blockNumber);
      group.style.cursor = 'pointer';
      group.style.pointerEvents = 'all';
    });

    const defs = svg.querySelector('defs');
    if (defs) {
      defs.remove();
    }
  }

  private bindFlatShapeMode(svg: SVGSVGElement, shapes: SVGElement[]): void {
    const existingMappings = new Map(this.blockMappings);
    this.blockMappings = new Map();

    shapes.forEach((shape: SVGElement, index: number) => {
      const blockNumber = this.padBlockNumber(index + 1);
      const blockId = `F${blockNumber}`;
      const existing = existingMappings.get(blockId);

      shape.id = blockId;
      shape.classList.add('interactive-block');
      shape.setAttribute('data-block-number', blockNumber);
      shape.style.cursor = 'pointer';
      shape.style.pointerEvents = 'all';

      this.blockMappings.set(blockId, this.buildBlockMappingFromExisting(blockId, blockNumber, existing));
    });
  }

  private buildBlockMappingFromExisting(
    blockId: string,
    blockNumber: string,
    existing?: BlockMapping
  ): BlockMapping {
    return {
      blockId,
      blockNumber,
      expectedUnitSerial: existing && existing.expectedUnitSerial
        ? existing.expectedUnitSerial
        : this.getExpectedUnitSerial(blockNumber),
      unitId: existing && existing.unitId,
      unitSerial: existing && existing.unitSerial,
      unitArea: existing && existing.unitArea,
      unitBuilding: existing && existing.unitBuilding,
      unitFloor: existing && existing.unitFloor,
      unitStatus: existing && existing.unitStatus,
      unitPrice: existing && existing.unitPrice,
      unitPricePerMeter: existing && existing.unitPricePerMeter,
      unitGaragePrice: existing && existing.unitGaragePrice,
      unitMaintenanceFees: existing && existing.unitMaintenanceFees,
      unitPrices: existing && existing.unitPrices ? existing.unitPrices.slice() : undefined,
    };
  }

  private getInteractiveFlatShapes(svg: SVGSVGElement): SVGElement[] {
    const root = this.getFlatShapeRoot(svg);
    if (!root) {
      return [];
    }

    const supportedSelector = 'path, polygon, polyline, rect, circle, ellipse';
    const candidates = this.getFlatShapeCandidates(svg, root, supportedSelector);
    if (!candidates.length) {
      return [];
    }

    const isNamedFloorGroup = !this.isGenericSvgLayer(root);
    if (isNamedFloorGroup) {
      return candidates;
    }

    const filteredByUnitFill = this.filterUnitLikeShapes(svg, candidates);
    if (filteredByUnitFill.length) {
      return filteredByUnitFill;
    }

    return candidates.filter((shape) => shape.classList.contains('st0') || shape.classList.contains('st1'));
  }

  private getFlatShapeCandidates(svg: SVGSVGElement, root: SVGElement, selector: string): SVGElement[] {
    const candidates = Array.from(root.querySelectorAll(selector))
      .filter((el): el is SVGElement => el instanceof SVGElement);

    const namedGroupIdByLevel: { [key: string]: string } = {
      'lower-ground': 'ground_floor',
      'upper-ground': 'upper_ground_floor',
      'floor-1': 'first_floor',
    };
    const namedGroupId = namedGroupIdByLevel[this.selectedMasterplanKey];

    if (namedGroupId && root.id === namedGroupId) {
      const topLevelSiblingShapes = Array.from(svg.children)
        .filter((el): el is SVGElement =>
          el instanceof SVGElement &&
          ['path', 'polygon', 'polyline', 'rect', 'circle', 'ellipse'].includes(el.tagName.toLowerCase())
        );

      if (topLevelSiblingShapes.length) {
        const merged = new Set<SVGElement>([...candidates, ...topLevelSiblingShapes]);
        return Array.from(merged).sort((a: SVGElement, b: SVGElement) => {
          const position = a.compareDocumentPosition(b);
          if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
            return -1;
          }
          if (position & Node.DOCUMENT_POSITION_PRECEDING) {
            return 1;
          }
          return 0;
        });
      }
    }

    return candidates;
  }

  private getFlatShapeRoot(svg: SVGSVGElement): SVGElement | null {
    const layer = svg.querySelector('#Layer_2') as SVGElement | null;
    const namedGroupIdByLevel: { [key: string]: string } = {
      'lower-ground': 'ground_floor',
      'upper-ground': 'upper_ground_floor',
      'floor-1': 'first_floor',
    };

    const namedGroupId = namedGroupIdByLevel[this.selectedMasterplanKey];
    if (layer && namedGroupId) {
      const namedGroup = layer.querySelector(`#${namedGroupId}`) as SVGElement | null;
      if (namedGroup) {
        return namedGroup;
      }
    }

    const candidateRoots = Array.from(svg.children)
      .filter((el): el is SVGElement => el instanceof SVGElement && el.tagName.toLowerCase() === 'g');
    if (layer && candidateRoots.indexOf(layer) === -1) {
      candidateRoots.push(layer);
    }

    let bestRoot: SVGElement | null = null;
    let bestScore = 0;

    candidateRoots.forEach((candidateRoot: SVGElement) => {
      const score = this.scoreFlatShapeRoot(svg, candidateRoot);
      if (score > bestScore) {
        bestScore = score;
        bestRoot = candidateRoot;
      }
    });

    return bestRoot || layer;
  }

  private scoreFlatShapeRoot(svg: SVGSVGElement, root: SVGElement): number {
    const supportedSelector = 'path, polygon, polyline, rect, circle, ellipse';
    const candidates = Array.from(root.querySelectorAll(supportedSelector))
      .filter((el): el is SVGElement => el instanceof SVGElement);
    if (!candidates.length) {
      return 0;
    }

    const unitLikeCandidates = this.filterUnitLikeShapes(svg, candidates);
    if (unitLikeCandidates.length) {
      return unitLikeCandidates.length * 100;
    }

    return candidates.length;
  }

  private filterUnitLikeShapes(svg: SVGSVGElement, shapes: SVGElement[]): SVGElement[] {
    const classFillMap = this.getSvgClassFillMap(svg);
    if (!Object.keys(classFillMap).length) {
      return [];
    }

    const unitLikeClasses = Object.keys(classFillMap).filter((className: string) => this.isUnitShapeFill(classFillMap[className]));
    if (!unitLikeClasses.length) {
      return [];
    }

    return shapes.filter((shape: SVGElement) => unitLikeClasses.some((className: string) => shape.classList.contains(className)));
  }

  private getSvgClassFillMap(svg: SVGSVGElement): { [className: string]: string } {
    const map: { [className: string]: string } = {};
    const styleContent = Array.from(svg.querySelectorAll('style'))
      .map((styleEl: Element) => styleEl.textContent || '')
      .join('\n');

    if (!styleContent) {
      return map;
    }

    const classRuleRegex = /([^{}]+)\{([^}]*)\}/g;
    let classRuleMatch: RegExpExecArray | null;

    while ((classRuleMatch = classRuleRegex.exec(styleContent)) !== null) {
      const selector = classRuleMatch[1];
      const declarations = classRuleMatch[2];
      const fillMatch = declarations.match(/fill\s*:\s*([^;]+)/i);
      if (!fillMatch) {
        continue;
      }

      const fillValue = fillMatch[1].trim().toLowerCase();
      const classNames = selector.match(/\.([a-zA-Z0-9_-]+)/g) || [];
      classNames.forEach((className: string) => {
        map[className.slice(1)] = fillValue;
      });
    }

    return map;
  }

  private isUnitShapeFill(fillValue: string): boolean {
    return [
      '#f7abaf',
      '#f7a8af',
      '#f7b0b4',
      '#f7b2b6',
      '#f7adad',
      'rgb(247, 171, 175)',
      '#9fcdf5',
      '#a0cdf5',
      '#9ecdf5',
      'rgb(159, 205, 245)',
      '#e0cdad',
      '#dfcdad',
      '#e1cead',
      'rgb(224, 205, 173)'
    ]
      .indexOf(fillValue) !== -1;
  }

  private isGenericSvgLayer(element: SVGElement): boolean {
    const elementId = element.id || '';
    return !elementId || /^Layer_\d+$/i.test(elementId) || elementId === 'body_1';
  }

  private attachDelegatedSvgHandlers(svg: SVGSVGElement): void {
    if (this.svgWithDelegatedHandlers && this.svgWithDelegatedHandlers !== svg) {
      this.detachDelegatedSvgHandlers();
    }

    if (this.svgWithDelegatedHandlers === svg) {
      return;
    }

    this.svgWithDelegatedHandlers = svg;

    this.svgPointerOverHandler = (ev: PointerEvent) => {
      const interactiveElement = this.findInteractiveElement(ev.target, svg);
      if (!interactiveElement) {
        this.clearHoveredPath();
        return;
      }
      const mapping = this.blockMappings.get(interactiveElement.id);
      if (!this.isBlockInteractiveInCurrentMode(mapping)) {
        this.clearHoveredPath();
        return;
      }
      const target = this.resolveHoverElement(ev.target, interactiveElement);
      if (!target || this.hoveredPathElement === target) {
        return;
      }
      this.clearHoveredPath();
      this.hoveredInteractiveElement = interactiveElement;
      this.hoveredPathElement = target;
      this.hoveredPathElement.classList.add('is-hovered');
      this.updateHoverTooltip(interactiveElement, ev);
    };

    this.svgPointerOutHandler = (ev: PointerEvent) => {
      if (!this.hoveredPathElement) {
        return;
      }
      const related = ev.relatedTarget as EventTarget | null;
      if (this.findInteractiveElement(related, svg) === this.hoveredInteractiveElement) {
        // Still inside the same group — only clear if moved to a different path
        const relatedEl = related as SVGElement | null;
        if (relatedEl && relatedEl !== this.hoveredPathElement) {
          this.clearHoveredPath();
        }
        return;
      }
      this.clearHoveredPath();
    };

    this.svgPointerMoveHandler = (ev: PointerEvent) => {
      if (!this.hoveredInteractiveElement || !this.hoverTooltip) {
        return;
      }
      this.updateHoverTooltip(this.hoveredInteractiveElement, ev);
    };

    this.svgClickHandler = (ev: MouseEvent) => {
      const interactiveElement = this.findInteractiveElement(ev.target, svg);
      if (!interactiveElement) {
        return;
      }
      const blockId = interactiveElement.id;
      const mapping = this.blockMappings.get(blockId);
      if (!mapping || !this.isBlockInteractiveInCurrentMode(mapping)) {
        return;
      }
      ev.preventDefault();
      this.setSelectedElement(interactiveElement);
      this.onBlockClick(blockId);
    };

    svg.addEventListener('pointerover', this.svgPointerOverHandler);
    svg.addEventListener('pointerout', this.svgPointerOutHandler);
    svg.addEventListener('pointermove', this.svgPointerMoveHandler);
    svg.addEventListener('click', this.svgClickHandler);
  }

  private clearHoveredPath(): void {
    if (this.hoveredPathElement) {
      this.hoveredPathElement.classList.remove('is-hovered');
      this.hoveredPathElement = null;
    }
    this.hoveredInteractiveElement = null;
    this.hoverTooltip = null;
  }

  private detachDelegatedSvgHandlers(): void {
    if (!this.svgWithDelegatedHandlers) {
      return;
    }
    if (this.svgPointerOverHandler) {
      this.svgWithDelegatedHandlers.removeEventListener('pointerover', this.svgPointerOverHandler);
    }
    if (this.svgPointerOutHandler) {
      this.svgWithDelegatedHandlers.removeEventListener('pointerout', this.svgPointerOutHandler);
    }
    if (this.svgPointerMoveHandler) {
      this.svgWithDelegatedHandlers.removeEventListener('pointermove', this.svgPointerMoveHandler);
    }
    if (this.svgClickHandler) {
      this.svgWithDelegatedHandlers.removeEventListener('click', this.svgClickHandler);
    }

    this.clearHoveredPath();

    this.clearSelectedElement();

    this.svgWithDelegatedHandlers = null;
    this.svgPointerOverHandler = null;
    this.svgPointerOutHandler = null;
    this.svgPointerMoveHandler = null;
    this.svgClickHandler = null;
  }

  private findInteractiveElement(target: EventTarget | null, svg: SVGSVGElement): SVGElement | null {
    let el = target as Element | null;
    while (el && el !== svg) {
      if (el instanceof SVGElement && el.classList.contains('interactive-block')) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }

  private resolveHoverElement(target: EventTarget | null, interactiveElement: SVGElement): SVGElement | null {
    if (target instanceof SVGElement) {
      const tagName = target.tagName.toLowerCase();
      if (['path', 'polygon', 'polyline', 'rect', 'circle', 'ellipse'].includes(tagName)) {
        return target;
      }
    }

    return interactiveElement;
  }

  private padBlockNumber(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }

  extractBlockNumber(blockId: string, element?: Element): string | null {
    if (!blockId && !element) {
      return null;
    }

    if (element && element.hasAttribute('data-block-number')) {
      const dataNum = element.getAttribute('data-block-number');
      if (dataNum && /^[0-9]+$/.test(dataNum)) {
        return this.padBlockNumber(parseInt(dataNum, 10));
      }
    }

    if (!blockId) {
      return null;
    }

    if (blockId.startsWith('LWPOLYLINE-')) {
      const numPart = blockId.replace('LWPOLYLINE-', '');
      if (/^[0-9]+$/.test(numPart)) {
        return this.padBlockNumber(parseInt(numPart, 10));
      }
    }

    const tokens = blockId.split('_').filter((t) => t.length > 0);
    const digits: string[] = [];

    for (const token of tokens) {
      const encodedDigit = token.match(/^x3([0-9])$/);
      if (encodedDigit) {
        digits.push(encodedDigit[1]);
        continue;
      }

      if (/^[0-9]+$/.test(token)) {
        if (!digits.length) {
          if (token.length > 3) {
            return null;
          }
          digits.push(...token.split(''));
          break;
        }

        const remaining = Math.max(0, 3 - digits.length);
        digits.push(...token.split('').slice(0, remaining));
        break;
      }

      break;
    }

    if (!digits.length) {
      return null;
    }

    const num = parseInt(digits.join(''), 10);
    if (Number.isNaN(num)) {
      return null;
    }

    return this.padBlockNumber(num);
  }

  onBlockClick(blockId: string): void {
    const mapping = this.blockMappings.get(blockId);
    if (!mapping) {
      return;
    }
    this.selectedBlock = mapping;
    this.selectedUnitId = mapping.unitId || null;
    this.availableUnits = this.selectedUnitId ? [this.toUnitOption(mapping)] : [];
    this.unitSearchError = null;
    this.saveError = null;

    if (this.isEditMode && this.canAccessModeToggle()) {
      this.showModal = true;
      this.showViewModal = false;
    } else {
      this.showViewModal = true;
      this.showModal = false;
      this.loadSelectedUnitDetails();
    }
  }

  getSelectedBlockUnitCode(): string | null {
    if (!this.selectedBlock) {
      return null;
    }

    return this.selectedBlock.unitSerial || this.selectedBlock.expectedUnitSerial || null;
  }

  private getExpectedUnitSerial(blockNumber: string): string | undefined {
    const parsedBlockNumber = parseInt(blockNumber, 10);
    if (Number.isNaN(parsedBlockNumber)) {
      return undefined;
    }

    const normalizedBlockNumber = this.padBlockNumber(parsedBlockNumber);
    const expectedUnitSerialsByLevel: { [level: string]: { [blockNumber: string]: string } } = {
      'floor-4': {
        '16': 'S424/C',
      },
    };
    const levelSerials = expectedUnitSerialsByLevel[this.selectedMasterplanKey];
    return levelSerials ? levelSerials[normalizedBlockNumber] : undefined;
  }

  closeModal(): void {
    this.showModal = false;
    this.showViewModal = false;
    this.selectedBlock = null;
    this.selectedUnitId = null;
    this.availableUnits = [];
    this.unitSearchError = null;
    this.saveError = null;
    this.isUnitSearchLoading = false;
    this.isViewUnitLoading = false;
    this.unitDetails = null;
    this.currentLayoutSlide = 0;
    this.unitComment = '';
    this.resetBlockForm();
    this.activeActionPanel = null;
    this.resetInlinePaymentState();
    this.clearSelectedElement();
  }

  saveUnitMapping(): void {
    if (!this.canAccessModeToggle()) {
      this.saveError = 'Only admins can edit masterplan mappings.';
      this.isEditMode = false;
      return;
    }

    if (!this.selectedBlock || !this.selectedUnitId) {
      return;
    }

    const unit = this.findSelectedUnit();
    this.saveError = null;

    this.projectsService.assignMasterplanProjectUnit(this.projectId, {
      path_id: this.selectedBlock.blockId,
      unit_id: this.selectedUnitId,
      level: this.selectedMasterplanKey
    }).subscribe(
      (res: any) => {
        const responseUnit = res && res.data && res.data.unit ? res.data.unit : unit;
        this.blockMappings.set(
          this.selectedBlock.blockId,
          this.toBlockMapping(this.selectedBlock.blockId, this.selectedBlock.blockNumber, responseUnit)
        );
        this.scheduleSvgStateSync();
        this.notificationService.success(
          'Masterplan updated',
          res && res.message ? res.message : 'Unit assigned successfully.'
        );
        this.closeModal();
      },
      (error: any) => {
        this.saveError = error && error.error && error.error.message
          ? error.error.message
          : 'Unit assignment failed.';
      }
    );
  }

  viewUnit(unitId: number): void {
    this.router.navigate(['/pages/projects/view-unit'], {
      queryParams: { project_id: this.projectId, unit_id: unitId }
    });
  }

  changeLayoutSlide(n: number): void {
    const images = this.unitDetails && this.unitDetails.layout_images;
    if (!images || !images.length) {
      return;
    }
    if (n > images.length - 1) {
      this.currentLayoutSlide = 0;
    } else if (n < 0) {
      this.currentLayoutSlide = images.length - 1;
    } else {
      this.currentLayoutSlide = n;
    }
  }

  openLayoutZoom(url: string): void {
    if (!url) {
      return;
    }
    this.imageZoomService.open(url);
  }

  private loadSelectedUnitDetails(): void {
    if (!this.selectedBlock || !this.selectedBlock.unitId) {
      this.unitDetails = null;
      this.resetInlinePaymentState();
      return;
    }

    this.isViewUnitLoading = true;
    this.currentLayoutSlide = 0;
    this.projectsService.getUnitDetails(this.selectedBlock.unitId).subscribe(
      (data: any) => {
        this.unitDetails = data;
        this.syncSelectedBlockStatus(data && data.status ? data.status : null);
        this.syncSelectedBlockPricing(data);
        this.seedInlinePaymentForm(data);
        this.isViewUnitLoading = false;
      },
      () => {
        this.unitDetails = null;
        this.resetInlinePaymentState();
        this.isViewUnitLoading = false;
      }
    );
  }

  private loadBlockSettings(): void {
    this.projectsService.getBlockSettings().subscribe(
      (res: any) => {
        this.blockSettingsTimes = res && res.times ? res.times : [];
        this.blockSettingsReasons = res && res.reasons ? res.reasons : [];
        this.blockSettingsAgents = res && res.users ? res.users : [];
      },
      () => {
        this.blockSettingsTimes = [];
        this.blockSettingsReasons = [];
        this.blockSettingsAgents = [];
      }
    );
  }

  private resetBlockForm(): void {
    this.blockFormModel = {
      time: '',
      reason: '',
      user_id: '',
      reason_comment: ''
    };
  }

  private syncSelectedBlockStatus(status: string | null): void {
    if (!this.selectedBlock) {
      return;
    }

    const current = this.blockMappings.get(this.selectedBlock.blockId);
    if (!current) {
      return;
    }

    this.blockMappings.set(this.selectedBlock.blockId, Object.assign({}, current, {
      unitStatus: status || undefined
    }));

    this.selectedBlock = this.blockMappings.get(this.selectedBlock.blockId) || this.selectedBlock;
    this.scheduleSvgStateSync();
  }

  private syncSelectedBlockPricing(unit: any): void {
    if (!this.selectedBlock || !unit) {
      return;
    }

    const current = this.blockMappings.get(this.selectedBlock.blockId);
    if (!current) {
      return;
    }

    const unitPrices = this.getUnitPriceEntries(unit);
    this.blockMappings.set(this.selectedBlock.blockId, Object.assign({}, current, {
      unitPrice: unitPrices.length ? unitPrices[0].value : unit.price,
      unitPricePerMeter: unitPrices.length ? unitPrices[0].pricePerMeter : unit.price_per_meter,
      unitGaragePrice: unit.project && unit.project.garage_price,
      unitMaintenanceFees: unit.maintenance_fees,
      unitPrices: unitPrices,
    }));

    this.selectedBlock = this.blockMappings.get(this.selectedBlock.blockId) || this.selectedBlock;
  }
  private setSelectedElement(element: SVGElement): void {
    if (this.selectedInteractiveElement === element) {
      return;
    }
    this.clearSelectedElement();
    this.selectedInteractiveElement = element;
    this.selectedInteractiveElement.classList.add('is-selected');
  }

  private clearSelectedElement(): void {
    if (!this.selectedInteractiveElement) {
      return;
    }
    this.selectedInteractiveElement.classList.remove('is-selected');
    this.selectedInteractiveElement = null;
  }

  private updateHoverTooltip(element: SVGElement, ev: PointerEvent): void {
    const mapping = this.blockMappings.get(element.id);
    if (!mapping || !mapping.unitId || !mapping.unitSerial || !mapping.unitStatus) {
      this.hoverTooltip = null;
      return;
    }

    const container = this.svgContainer && this.svgContainer.nativeElement;
    if (!container) {
      this.hoverTooltip = null;
      return;
    }

    const rect = (container as HTMLElement).getBoundingClientRect();
    this.hoverTooltip = {
      blockId: mapping.blockId,
      unitSerial: mapping.unitSerial,
      status: mapping.unitStatus,
      price: mapping.unitPrice,
      pricePerMeter: mapping.unitPricePerMeter,
      garagePrice: mapping.unitGaragePrice,
      maintenanceFees: mapping.unitMaintenanceFees,
      prices: mapping.unitPrices || [],
      x: ev.clientX - rect.left + 14,
      y: ev.clientY - rect.top + 14,
    };
  }

  private scheduleSvgStateSync(attempt: number = 0): void {
    if (this.svgStateSyncTimer) {
      clearTimeout(this.svgStateSyncTimer);
      this.svgStateSyncTimer = null;
    }

    this.svgStateSyncTimer = setTimeout(() => {
      this.svgStateSyncTimer = null;
      const synced = this.applyStatusColorsToSvg();
      if (!synced && attempt < 6) {
        this.scheduleSvgStateSync(attempt + 1);
      }
    }, attempt === 0 ? 0 : 80);
  }

  private applyStatusColorsToSvg(): boolean {
    const container = this.svgContainer && this.svgContainer.nativeElement;
    if (!container) {
      return false;
    }

    const svg = (container as HTMLElement).querySelector('svg') as SVGSVGElement | null;
    if (!svg) {
      return false;
    }

    const interactiveElements = Array.from(svg.querySelectorAll('.interactive-block'))
      .filter((el): el is SVGElement => el instanceof SVGElement);
    if (!interactiveElements.length) {
      return false;
    }

    interactiveElements.forEach((element: SVGElement) => {
      const mapping = this.blockMappings.get(element.id);
      this.applyStatusColorToElement(element, mapping);
    });

    return true;
  }

  private applyStatusColorToElement(element: SVGElement, mapping?: BlockMapping): void {
    const status = mapping && mapping.unitStatus ? mapping.unitStatus : null;
    const color = status ? this.getStatusColor(status) : null;
    const shapeElements = this.getStatusTargetShapes(element);
    const isInteractive = this.isBlockInteractiveInCurrentMode(mapping);
    const isUnlinkedInShowMode = !this.isEditMode && !isInteractive;

    element.removeAttribute('data-unit-status');
    element.classList.toggle('is-unlinked', isUnlinkedInShowMode);
    element.style.pointerEvents = isInteractive ? 'all' : 'none';
    element.style.cursor = isInteractive ? 'pointer' : 'default';
    if (status) {
      element.setAttribute('data-unit-status', status);
    }

    shapeElements.forEach((shape: SVGElement) => {
      if (isUnlinkedInShowMode) {
        shape.style.fill = 'rgba(255, 255, 255, 0.68)';
        shape.style.removeProperty('stroke');
        shape.style.removeProperty('stroke-width');
        shape.style.removeProperty('vector-effect');
        return;
      }

      if (!color) {
        shape.style.removeProperty('fill');
        shape.style.removeProperty('stroke');
        shape.style.removeProperty('stroke-width');
        shape.style.removeProperty('vector-effect');
        return;
      }

      shape.style.fill = this.hexToRgba(color, 0.68);
      shape.style.removeProperty('stroke');
      shape.style.removeProperty('stroke-width');
      shape.style.removeProperty('vector-effect');
    });
  }

  private isBlockInteractiveInCurrentMode(mapping?: BlockMapping): boolean {
    if (this.isEditMode) {
      return true;
    }

    return !!(mapping && mapping.unitId && mapping.unitSerial);
  }

  private getStatusTargetShapes(element: SVGElement): SVGElement[] {
    const tagName = element.tagName.toLowerCase();
    if (['path', 'polygon', 'polyline', 'rect', 'circle', 'ellipse'].includes(tagName)) {
      return [element];
    }

    const descendants = Array.from(element.querySelectorAll(this.interactiveShapeSelector))
      .filter((shape): shape is SVGElement => shape instanceof SVGElement);

    return descendants.length ? descendants : [element];
  }

  private getStatusColor(status: string): string | null {
    const normalizedStatus = status === 'Non Available' ? 'Not Available' : status;
    const match = this.statusLegend.find((item: StatusLegendItem) => item.label === normalizedStatus);
    return match ? match.color : null;
  }

  getStatusBadgeBackground(status: string | null | undefined): string | null {
    if (!status) {
      return null;
    }
    return this.getStatusColor(status);
  }

  getStatusBadgeTextColor(status: string | null | undefined): string {
    if (!status) {
      return '#ffffff';
    }
    const normalizedStatus = status === 'Non Available' ? 'Not Available' : status;
    const match = this.statusLegend.find((item: StatusLegendItem) => item.label === normalizedStatus);
    return match && match.textColor ? match.textColor : '#ffffff';
  }

  private hexToRgba(hex: string, alpha: number): string {
    const normalized = (hex || '').replace('#', '');
    const fullHex = normalized.length === 3
      ? normalized.split('').map((char: string) => char + char).join('')
      : normalized;

    if (!/^[0-9a-fA-F]{6}$/.test(fullHex)) {
      return hex;
    }

    const red = parseInt(fullHex.slice(0, 2), 16);
    const green = parseInt(fullHex.slice(2, 4), 16);
    const blue = parseInt(fullHex.slice(4, 6), 16);

    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  }

  getUnitOptionLabel(unit: any): string {
    if (!unit) {
      return '';
    }

    return unit.serial || unit.unit_serial || unit.unit_no || unit.unit_number || `${unit.id}`;
  }

  isSalesTeamUser(): boolean {
    const salesFlag = this.userProfile ? this.userProfile.is_sales_team : undefined;
    return this.truthySalesTeamFlags.includes(salesFlag);
  }

  private getNormalizedUnitStatus(): string {
    const status = this.unitDetails && this.unitDetails.status
      ? `${this.unitDetails.status}`.trim()
      : '';
    return status === 'Non Available' ? 'Not Available' : status;
  }

  canChangeUnitStatus(): boolean {
    const email = this.userProfile && this.userProfile.email
      ? `${this.userProfile.email}`.trim().toLowerCase()
      : '';
    return !!email && this.unitStatusActionEmails.indexOf(email) !== -1;
  }

  canBlockUnit(): boolean {
    if (!this.unitDetails || !this.canChangeUnitStatus()) {
      return false;
    }

    const status = this.getNormalizedUnitStatus();
    return status === 'Available' || status === 'Not Available';
  }

  canMakeUnitAvailable(): boolean {
    if (!this.unitDetails || !this.canChangeUnitStatus()) {
      return false;
    }

    const status = this.getNormalizedUnitStatus();
    return status === 'Not Available' || status === 'Blocked';
  }

  canReserveUnit(): boolean {
    const unit = this.unitDetails;
    if (!unit || !this.userProfile || this.userProfile.role === 'Engineer' || this.userProfile.role === 'Moderator') {
      return false;
    }

    return (
      (
        ((unit.status === 'Available' || unit.status === 'Blocked' || unit.status === 'Temp Blocked') &&
          (this.userProfile.role === 'Admin' || this.userProfile.role === 'CFO')) ||
        (this.userProfile.role !== 'Contractor' &&
          this.userProfile.role !== 'CCO' &&
          unit.status === 'Available') ||
        (unit.status === 'Temp Blocked' && unit.temp_block_by === this.userProfile.id) ||
        (unit.status === 'Blocked' && unit.block_by === this.userProfile.id)
      ) && !this.isSalesTeamUser()
    );
  }

  canGeneratePayment(): boolean {
    return !!(this.userProfile && this.userProfile.role !== 'CCO' && this.userProfile.role !== 'Engineer');
  }

  canViewSummary(): boolean {
    return !!(this.userProfile && this.userProfile.role !== 'CCO');
  }

  canDeleteUnit(): boolean {
    return !!(this.userProfile && (this.userProfile.role === 'Admin' || this.userProfile.role === 'CFO'));
  }

  canMakeUnitNotAvailable(): boolean {
    if (!this.unitDetails || !this.canChangeUnitStatus()) {
      return false;
    }

    const status = this.getNormalizedUnitStatus();
    return status === 'Available' || status === 'Blocked';
  }

  canAddComment(): boolean {
    return !!(
      this.userProfile &&
      (
        this.userProfile.role === 'Admin' ||
        this.userProfile.role === 'CFO' ||
        (this.userProfile.role === 'Moderator' &&
          this.userProfile.role !== 'CCO' &&
          this.userProfile.role !== 'Engineer')
      )
    );
  }

  reserveSelectedUnit(): void {
    if (!this.unitDetails) {
      return;
    }

    if (this.userProfile.id === this.unitDetails.temp_block_by && this.unitDetails.status === 'Temp Blocked') {
      this.router.navigate(['pages/reservations/add/', this.unitDetails.id]);
      return;
    }

    swal({
      title: 'Are you sure?',
      text: 'You will reserve the unit!',
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, reserve it!',
      cancelButtonText: 'No, keep it',
    }).then((result: any) => {
      if (!result.value) {
        if (result.dismiss) {
          swal('Cancelled', '', 'error');
        }
        return;
      }

      this.slimLoadingBarService.start();
      this.stopReservation = true;
      this.reservationService.blockUnitAtReservation(this.unitDetails.id).subscribe(
        () => {
          this.stopReservation = false;
          this.slimLoadingBarService.complete();
          this.router.navigate(['pages/reservations/add/', this.unitDetails.id]);
        },
        () => {
          this.stopReservation = false;
          this.slimLoadingBarService.complete();
        }
      );
    });
  }

  submitBlockUnit(): void {
    if (!this.unitDetails) {
      return;
    }

    const payload: any = Object.assign({}, this.blockFormModel, {
      id: this.unitDetails.id
    });

    if (!payload.reason) {
      swal('Oops...', 'Reason can not be empty!', 'error');
      return;
    }

    if (!payload.time) {
      swal('Oops...', 'Time can not be empty!', 'error');
      return;
    }

    this.slimLoadingBarService.start();
    this.projectsService.requestBlockUnit(payload).subscribe(
      () => {
        swal('Woohoo!', this.userProfile.role === 'Sales Manager' ? 'Blocked request sent successfully!' : 'Blocked successfully!', 'success');
        this.slimLoadingBarService.complete();
        this.resetBlockForm();
        this.activeActionPanel = null;
        this.loadSelectedUnitDetails();
      },
      () => {
        this.slimLoadingBarService.complete();
      }
    );
  }

  addCommentToSelectedUnit(): void {
    if (!this.unitDetails || !this.unitComment || !this.unitComment.trim()) {
      swal('Error', 'Comment can not be empty', 'error');
      return;
    }

    this.slimLoadingBarService.start();
    this.projectsService.addUnitComment(this.unitDetails.id, this.unitComment.trim()).subscribe(
      () => {
        swal('Woohoo!', 'Comment have added succesfully!', 'success');
        this.unitComment = '';
        this.slimLoadingBarService.complete();
        this.activeActionPanel = null;
        this.loadSelectedUnitDetails();
      },
      () => {
        this.slimLoadingBarService.complete();
      }
    );
  }

  generatePaymentForSelectedUnit(): void {
    if (!this.unitDetails) {
      return;
    }
    this.router.navigate(['/pages/payment-generator'], {
      queryParams: { unit_id: this.unitDetails.id }
    });
  }

  deleteSelectedUnit(): void {
    if (!this.unitDetails) {
      return;
    }

    swal({
      title: 'Are you sure?',
      text: 'You will delete this unit!',
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, keep it.',
    }).then((result: any) => {
      if (!result.value) {
        if (result.dismiss) {
          swal('Cancelled', '', 'error');
        }
        return;
      }

      this.slimLoadingBarService.start();
      this.projectsService.deleteUnit(this.unitDetails.id).subscribe(
        () => {
          this.slimLoadingBarService.complete();
          this.notificationService.success('Unit deleted', 'The linked unit was deleted successfully.');
          this.closeModal();
          this.loadMasterPlanMappings();
        },
        () => {
          this.slimLoadingBarService.complete();
        }
      );
    });
  }

  makeSelectedUnitNotAvailable(): void {
    if (!this.unitDetails) {
      return;
    }

    swal({
      title: 'Are you sure?',
      text: 'You will make the unit un-available',
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, make it un-available!',
      cancelButtonText: 'No, keep it',
    }).then((result: any) => {
      if (!result.value) {
        if (result.dismiss) {
          swal('Cancelled', '', 'error');
        }
        return;
      }

      this.slimLoadingBarService.start();
      this.projectsService.makeUnitNotAvailable(this.unitDetails.id).subscribe(
        (data: any) => {
          this.unitDetails = data || this.unitDetails;
          this.slimLoadingBarService.complete();
          this.notificationService.success('Unit updated', 'Unit marked as not available.');
          this.loadSelectedUnitDetails();
        },
        () => {
          this.slimLoadingBarService.complete();
        }
      );
    });
  }

  makeSelectedUnitAvailable(): void {
    if (!this.unitDetails) {
      return;
    }

    swal({
      title: 'Are you sure?',
      text: 'You will make the unit available',
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, make it available!',
      cancelButtonText: 'No, keep it',
    }).then((result: any) => {
      if (!result.value) {
        if (result.dismiss) {
          swal('Cancelled', '', 'error');
        }
        return;
      }

      this.slimLoadingBarService.start();
      this.projectsService.makeUnitAvailable(this.unitDetails.id, { comment: '' }).subscribe(
        (data: any) => {
          this.unitDetails = data || this.unitDetails;
          this.slimLoadingBarService.complete();
          this.notificationService.success('Unit updated', 'Unit marked as available.');
          this.loadSelectedUnitDetails();
        },
        () => {
          this.slimLoadingBarService.complete();
        }
      );
    });
  }

  noop(): void {}

  toggleActionPanel(panel: 'block' | 'comment'): void {
    this.activeActionPanel = this.activeActionPanel === panel ? null : panel;

    if (this.activeActionPanel === 'block') {
      setTimeout(() => {
        const blockCard = document.querySelector('.unit-status-action-card');
        if (blockCard) {
          blockCard.scrollIntoView();
        }
      }, 0);
    }
  }

  isActionPanelOpen(panel: 'block' | 'comment'): boolean {
    return this.activeActionPanel === panel;
  }

  trackByStatusLabel(index: number, item: StatusLegendItem): string {
    return item.label;
  }

  getHoverTooltipStatusColor(): string | null {
    return this.hoverTooltip ? this.getStatusColor(this.hoverTooltip.status) : null;
  }

  openStandalonePaymentGenerator(): void {
    if (!this.unitDetails || !this.unitDetails.id) {
      return;
    }

    this.router.navigate(['/pages/payment-generator'], {
      queryParams: { unit_id: this.unitDetails.id }
    });
  }

  toggleInlinePaymentGenerator(): void {
    if (!this.unitDetails) {
      return;
    }

    this.showInlinePaymentForm = !this.showInlinePaymentForm;
    this.inlinePaymentError = null;
    if (!this.showInlinePaymentForm) {
      this.inlinePaymentGenerated = false;
    } else if (!this.inlinePaymentForm.unit_serial) {
      this.seedInlinePaymentForm(this.unitDetails);
    }
  }

  onInlineFinishTypeChange(id: number | null): void {
    this.inlinePaymentForm.finishing_type_id = id;
    this.inlinePaymentGenerated = false;
    this.inlinePaymentError = null;
    this.paymentMethods = [];
    this.inlinePaymentForm.type = null;
    this.inlinePaymentForm.percentage = '';

    if (!this.unitDetails) {
      return;
    }

    if (id) {
      (this.unitDetails.finishing_types || []).forEach((type: any) => {
        if (type.id == id) {
          this.inlinePaymentForm.price = type.pivot && type.pivot.price ? type.pivot.price : this.inlinePaymentForm.price;
        }
      });
    }

    const payload = this.getInlinePaymentTermsPayload();
    if (payload.project_id && payload.finishing_type_id) {
      this.userService.getProjectPaymentTerms(payload).subscribe(
        (res: any) => {
          this.paymentMethods = res || [];
        },
        () => {
          this.paymentMethods = [];
          this.inlinePaymentError = 'Failed to load payment methods.';
        }
      );
    }
  }

  onInlinePaymentMethodChange(methodId: any): void {
    this.inlinePaymentForm.type = methodId;
    this.inlinePaymentGenerated = false;
    this.inlinePaymentError = null;
    this.inlinePaymentForm.percentage = '';

    this.paymentMethods.forEach((method: any) => {
      if (method.id == methodId) {
        this.inlinePaymentForm.percentage = method.downpayment_percentage;
      }
    });
  }

  generateInlinePayment(): void {
    if (!this.unitDetails || !this.unitDetails.id) {
      return;
    }

    if (!this.inlinePaymentForm.finishing_type_id || !this.inlinePaymentForm.type) {
      this.inlinePaymentError = 'Finish type and payment method are required.';
      return;
    }

    this.inlinePaymentLoading = true;
    this.inlinePaymentGenerated = false;
    this.inlinePaymentError = null;

    this.paymentService.getInstallments(this.getInlineInstallmentsPayload()).subscribe(
      () => {
        this.inlinePaymentLoading = false;
        this.inlinePaymentGenerated = true;
      },
      (err: any) => {
        this.inlinePaymentLoading = false;
        this.inlinePaymentGenerated = false;
        this.inlinePaymentError = err && err.error && err.error.message
          ? err.error.message
          : 'Failed to generate payment offer.';
      }
    );
  }

  downloadInlinePaymentPdf(): void {
    if (!this.unitDetails || !this.inlinePaymentForm.type) {
      return;
    }

    let url = `${environment.api_raw_base_url}installments/pdf/${this.unitDetails.id}/${this.inlinePaymentForm.type}?generated_view=${this.inlinePaymentForm.generated_view}&slots=${this.inlinePaymentForm.slots}&service_room_slots=${this.inlinePaymentForm.service_room_slots}&service_room_with_bathroom_slots=${this.inlinePaymentForm.service_room_with_bathroom_slots}&finishing_type_id=${this.inlinePaymentForm.finishing_type_id}&extra_discount=${this.inlinePaymentForm.extra_discount}`;
    if (this.userProfile.role === 'Admin' || this.userProfile.role === 'CFO') {
      url += `&tot_amount_for_unit=${this.inlinePaymentForm.price}`;
    }
    url += `&starting_date=${this.inlinePaymentForm.starting_date}`;
    window.open(url);
  }

  downloadInlinePaymentExcel(): void {
    if (!this.unitDetails || !this.inlinePaymentForm.type) {
      return;
    }

    let url = `${environment.api_raw_base_url}installments/excel/${this.unitDetails.id}/${this.inlinePaymentForm.type}?generated_view=${this.inlinePaymentForm.generated_view}&slots=${this.inlinePaymentForm.slots}&service_room_slots=${this.inlinePaymentForm.service_room_slots}&service_room_with_bathroom_slots=${this.inlinePaymentForm.service_room_with_bathroom_slots}&finishing_type_id=${this.inlinePaymentForm.finishing_type_id}&extra_discount=${this.inlinePaymentForm.extra_discount}`;
    if (this.userProfile.role === 'Admin' || this.userProfile.role === 'CFO') {
      url += `&tot_amount_for_unit=${this.inlinePaymentForm.price}`;
    }
    url += `&starting_date=${this.inlinePaymentForm.starting_date}`;
    window.open(url);
  }

  openInlinePaymentOffer(): void {
    this.generatingInlineOfferPdf = true;
    this.paymentService.getOfferPdf(this.getInlineOfferPayload()).subscribe(
      (res: any) => {
        this.generatingInlineOfferPdf = false;
        window.open(`https://${res.url}`);
      },
      () => {
        this.generatingInlineOfferPdf = false;
        this.inlinePaymentError = 'Failed to generate offer preview.';
      }
    );
  }

  getPageTitle(): string {
    const baseTitle = this.isEditMode ? 'Dynamic Master Plan Setup' : 'Dynamic Master Plan View';
    return this.projectName ? `${baseTitle} - ${this.projectName}` : baseTitle;
  }

  canAccessModeToggle(): boolean {
    return !!(this.userProfile && this.userProfile.role === 'Admin');
  }

  private createInlinePaymentForm(): InlinePaymentFormModel {
    return {
      finishing_type_id: null,
      type: null,
      slots: 1,
      service_room_slots: 0,
      service_room_with_bathroom_slots: 0,
      generated_view: 'Total',
      price: '',
      percentage: '',
      status: '',
      extra_discount: '',
      starting_date: '',
      phase_id: '',
      unit_serial: '',
    };
  }

  private resetInlinePaymentState(): void {
    this.showInlinePaymentForm = false;
    this.inlinePaymentGenerated = false;
    this.inlinePaymentLoading = false;
    this.inlinePaymentError = null;
    this.generatingInlineOfferPdf = false;
    this.paymentMethods = [];
    this.inlinePaymentForm = this.createInlinePaymentForm();
  }

  private seedInlinePaymentForm(unitDetails: any): void {
    this.inlinePaymentGenerated = false;
    this.inlinePaymentLoading = false;
    this.inlinePaymentError = null;
    this.generatingInlineOfferPdf = false;
    this.paymentMethods = [];
    this.inlinePaymentForm = this.createInlinePaymentForm();
    this.inlinePaymentForm.unit_serial = unitDetails && unitDetails.serial ? unitDetails.serial : '';
    this.inlinePaymentForm.status = unitDetails && unitDetails.status ? unitDetails.status : '';
    this.inlinePaymentForm.extra_discount = unitDetails && unitDetails.extra_discount ? unitDetails.extra_discount : '';
    this.inlinePaymentForm.phase_id = unitDetails && unitDetails.phase_id ? unitDetails.phase_id : '';
    this.inlinePaymentForm.price = unitDetails && (unitDetails.price || unitDetails.new_price || unitDetails.build_area)
      ? (unitDetails.price || unitDetails.new_price || unitDetails.build_area)
      : '';
    this.inlinePaymentForm.starting_date = this.getTodayDateInputValue();
  }

  getFinishingPricePerMeter(unit: any, priceType: any): number | null {
    if (unit && unit.finishing_types_price_per_meter && priceType && priceType.id) {
      const finishingData = unit.finishing_types_price_per_meter[priceType.id];
      if (finishingData && finishingData.price_per_meter !== null && finishingData.price_per_meter !== undefined) {
        return finishingData.price_per_meter;
      }
    }
    return null;
  }

  private getUnitPriceEntries(unit: any): UnitPriceEntry[] {
    const prices: UnitPriceEntry[] = [];

    if (unit && unit.finishing_types && unit.finishing_types.length) {
      unit.finishing_types.forEach((priceType: any) => {
        const rawPrice = priceType && priceType.pivot && priceType.pivot.price !== undefined && priceType.pivot.price !== null
          ? priceType.pivot.price
          : priceType && priceType.price !== undefined && priceType.price !== null
            ? priceType.price
            : priceType && priceType.new_price !== undefined && priceType.new_price !== null
              ? priceType.new_price
              : null;

        if (rawPrice !== null && rawPrice !== undefined) {
          prices.push({
            label: priceType && priceType.name ? priceType.name : 'Price',
            value: rawPrice,
            pricePerMeter: this.getFinishingPricePerMeter(unit, priceType)
          });
        }
      });
    }

    if (!prices.length && unit && unit.price !== null && unit.price !== undefined) {
      prices.push({
        label: 'Core and Shell',
        value: unit.price,
        pricePerMeter: unit.price_per_meter
      });
    }

    return prices;
  }

  private getInlinePaymentTermsPayload(): any {
    return {
      project_id: this.unitDetails && this.unitDetails.project ? this.unitDetails.project.id : null,
      phase_id: this.inlinePaymentForm.phase_id,
      finishing_type_id: this.inlinePaymentForm.finishing_type_id,
    };
  }

  private getInlineInstallmentsPayload(): any {
    return {
      down_payment: this.inlinePaymentForm.percentage,
      slots: this.inlinePaymentForm.slots,
      service_room_slots: this.inlinePaymentForm.service_room_slots,
      service_room_with_bathroom_slots: this.inlinePaymentForm.service_room_with_bathroom_slots,
      type: this.inlinePaymentForm.type,
      unit_serial: this.inlinePaymentForm.unit_serial,
      unit_id: this.unitDetails.id,
      generated_view: this.inlinePaymentForm.generated_view,
      price: this.inlinePaymentForm.price,
      starting_date: this.inlinePaymentForm.starting_date,
      finishing_type_id: this.inlinePaymentForm.finishing_type_id,
      extra_discount: this.inlinePaymentForm.extra_discount,
    };
  }

  private getInlineOfferPayload(): any {
    return Object.assign({}, this.inlinePaymentForm, {
      unit_id: this.unitDetails.id,
    });
  }

  private getTodayDateInputValue(): string {
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
  }

  private findSelectedUnit(): any {
    return this.availableUnits.find((unit: any) => unit && unit.id === this.selectedUnitId) || null;
  }

  private mergeUnitsWithSelected(units: any[]): any[] {
    const nextUnits = Array.isArray(units) ? units.slice() : [];
    const selectedUnit = this.selectedBlock && this.selectedBlock.unitId
      ? this.toUnitOption(this.selectedBlock)
      : null;

    if (!selectedUnit) {
      return nextUnits;
    }

    if (!nextUnits.some((unit: any) => unit && unit.id === selectedUnit.id)) {
      nextUnits.unshift(selectedUnit);
    }

    return nextUnits;
  }

  private toUnitOption(unit: any): any {
    return {
      id: unit.unitId || unit.id,
      serial: unit.unitSerial || unit.serial || unit.unit_serial || unit.unit_no || unit.unit_number,
      building: unit.unitBuilding || unit.building || unit.building_name,
      floor: unit.unitFloor || unit.floor || unit.floor_name,
      gross_area: unit.unitArea || unit.gross_area || unit.area,
      status: unit.unitStatus || unit.status,
    };
  }

  private toBlockMapping(blockId: string, blockNumber: string, unit: any): BlockMapping {
    const unitPrices = this.getUnitPriceEntries(unit);
    return {
      blockId,
      blockNumber,
      expectedUnitSerial: this.getExpectedUnitSerial(blockNumber),
      unitId: unit && unit.id,
      unitSerial: unit && this.getUnitOptionLabel(unit),
      unitArea: unit && (unit.gross_area || unit.area),
      unitBuilding: unit && (unit.building || unit.building_name),
      unitFloor: unit && (unit.floor || unit.floor_name),
      unitStatus: unit && unit.status,
      unitPrice: unitPrices.length ? unitPrices[0].value : unit && unit.price,
      unitPricePerMeter: unitPrices.length ? unitPrices[0].pricePerMeter : unit && unit.price_per_meter,
      unitGaragePrice: unit && unit.project && unit.project.garage_price,
      unitMaintenanceFees: unit && unit.maintenance_fees,
      unitPrices: unitPrices,
    };
  }

  goBack(): void {
    this.router.navigate(['/pages/projects']);
  }
}



// WEBPACK FOOTER //
// ./src/app/setup-dynamic-masterplan/setup-dynamic-masterplan.component.ts